import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dumbbell,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Trophy,
  Zap,
  Lock,
  Calendar,
  RotateCcw,
  MessageSquare,
  Send,
  X,
  PenLine,
  Play,
  Lightbulb,
  Camera,
  Image as ImageIcon,
  RefreshCw,
  Timer,
  TrendingUp,
  Repeat,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { WorkoutSkeleton } from '../components/Skeletons';
import { RestTimerPicker, RestTimerBar, readRestState, clearRestState } from '../components/RestTimer';
import { ExerciseHistoryModal } from '../components/ExerciseHistoryModal';
import { SubstitutesModal } from '../components/SubstitutesModal';
import { ExerciseMovementImages } from '../components/ExerciseMovementImages';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

const CACHE_KEY = 'ferraz_workout_cache';

const saveToCache = (plan, prog, notes) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      workoutPlan: plan,
      progress: prog,
      exerciseNotes: notes,
      cachedAt: Date.now()
    }));
  } catch (e) { console.warn('[WorkoutPlanPage] non-critical failure', e); }
};

const loadFromCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

export const WorkoutPlanPage = ({ user, onUserUpdate }) => {
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);
  const [completingExercise, setCompletingExercise] = useState(null);
  const [completingDay, setCompletingDay] = useState(null); // Lock entire day during exercise completion
  const [showFeedbackModal, setShowFeedbackModal] = useState(null); // day_id when showing
  const [feedbackText, setFeedbackText] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [exerciseNotes, setExerciseNotes] = useState({});
  const [savingNote, setSavingNote] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null); // ID do exercício com vídeo expandido
  const [expandedInstruction, setExpandedInstruction] = useState(null); // ID do exercício com instrução expandida

  // Rest Timer state
  const [restPickerFor, setRestPickerFor] = useState(null); // { id, name } | null
  const [restTimer, setRestTimer] = useState(() => {
    // Restore active rest timer state after app close/reopen or refresh
    const s = readRestState();
    if (!s) return null;
    const stillHasTime = s.paused
      ? (s.pausedRemainingMs || 0) > 0
      : (s.endTime - Date.now()) > 0;
    if (!stillHasTime && !s.paused) {
      // Finished while away — clear silently
      clearRestState();
      return null;
    }
    return { seconds: s.initialSeconds, exerciseName: s.exerciseName || '', key: s.key };
  });
  const [lastRestSeconds, setLastRestSeconds] = useState(60);

  // Load tracking (progressive overload)
  const [exerciseLoads, setExerciseLoads] = useState({}); // { [exerciseId]: { weight_kg, reps } }
  const [savingLoad, setSavingLoad] = useState(null);
  const [historyModal, setHistoryModal] = useState(null); // { id, name } | null
  const [substitutesModal, setSubstitutesModal] = useState(null); // { name, ids } | null

  const fetchWorkoutData = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    setFetchError(false);
    try {
      const plansResponse = await axios.get(`${API}/workout-plans`);
      if (plansResponse.data.length > 0) {
        const plan = plansResponse.data[0];
        setWorkoutPlan(plan);

        const [progressResponse, notesResponse, loadsResponse] = await Promise.all([
          axios.get(`${API}/workout-plans/${plan.id}/progress`),
          axios.get(`${API}/workout-plans/${plan.id}/notes`),
          axios.get(`${API}/workout-plans/${plan.id}/loads`).catch(() => ({ data: {} })),
        ]);
        setProgress(progressResponse.data);
        setExerciseNotes(notesResponse.data);
        setExerciseLoads(loadsResponse.data || {});

        // Save to cache for offline use
        saveToCache(plan, progressResponse.data, notesResponse.data);

        if (!isRefresh && plan.days?.length === 1) {
          setExpandedDay(plan.days[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching workout data:', error);
      // Try to load from cache when network fails
      if (!isRefresh) {
        const cached = loadFromCache();
        if (cached?.workoutPlan) {
          setWorkoutPlan(cached.workoutPlan);
          setProgress(cached.progress || []);
          setExerciseNotes(cached.exerciseNotes || {});
          if (cached.workoutPlan.days?.length === 1) {
            setExpandedDay(cached.workoutPlan.days[0].id);
          }
        } else {
          setFetchError(true);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load cache first for instant display, then fetch fresh data
    const cached = loadFromCache();
    if (cached?.workoutPlan) {
      setWorkoutPlan(cached.workoutPlan);
      setProgress(cached.progress || []);
      setExerciseNotes(cached.exerciseNotes || {});
      setLoading(false);
      if (cached.workoutPlan.days?.length === 1) {
        setExpandedDay(cached.workoutPlan.days[0].id);
      }
      // Refresh in background
      fetchWorkoutData(true);
    } else {
      fetchWorkoutData();
    }
  }, [fetchWorkoutData]);

  // Refetch when tab becomes visible (user returns to app)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchWorkoutData(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchWorkoutData]);

  const saveExerciseNote = async (dayId, exerciseId, note) => {
    const key = `${dayId}_${exerciseId}`;
    setSavingNote(key);
    
    try {
      await axios.post(
        `${API}/workout-plans/${workoutPlan.id}/days/${dayId}/exercises/${exerciseId}/notes`,
        { note }
      );
      setExerciseNotes(prev => ({ ...prev, [key]: note }));
      toast.success('Anotação salva!', { duration: 1500 });
    } catch (error) {
      toast.error('Erro ao salvar anotação');
    } finally {
      setSavingNote(null);    }
  };

  const saveExerciseLoad = async (dayId, exerciseId, fields) => {
    setSavingLoad(exerciseId);
    try {
      await axios.post(
        `${API}/workout-plans/${workoutPlan.id}/days/${dayId}/exercises/${exerciseId}/load`,
        fields
      );
      setExerciseLoads((prev) => ({
        ...prev,
        [exerciseId]: { ...(prev[exerciseId] || {}), ...fields },
      }));
    } catch {
      toast.error('Erro ao salvar carga');
    } finally {
      setSavingLoad(null);
    }
  };

  const isExerciseCompleted = (dayId, exerciseId) => {
    const dayProgress = progress.find((p) => p.workout_day_id === dayId);
    return dayProgress?.completed_exercises?.includes(exerciseId) || false;
  };

  const isDayCompleted = (dayId) => {
    const dayProgress = progress.find((p) => p.workout_day_id === dayId);
    return !!dayProgress?.completed_at;
  };

  const getDayProgress = (day) => {
    const dayProgress = progress.find((p) => p.workout_day_id === day.id);
    const completedCount = dayProgress?.completed_exercises?.length || 0;
    const totalCount = day.exercises?.length || 0;
    return { completed: completedCount, total: totalCount };
  };

  const completeExercise = async (dayId, exerciseId) => {
    const alreadyCompleted = isExerciseCompleted(dayId, exerciseId);

    // If day is fully done, block uncomplete
    if (alreadyCompleted && isDayCompleted(dayId)) {
      toast.info('Treino já finalizado esta semana!');
      return;
    }

    // Prevent concurrent exercise completions for the same day
    if (completingDay === dayId) return;

    setCompletingExercise(exerciseId);
    setCompletingDay(dayId);

    try {
      if (alreadyCompleted) {
        // UNCOMPLETE
        await axios.delete(
          `${API}/workout-plans/${workoutPlan.id}/days/${dayId}/exercises/${exerciseId}/complete`
        );
        toast.info('Exercício desmarcado');
      } else {
        // COMPLETE
        if (isDayCompleted(dayId)) {
          toast.error('Você já completou este treino esta semana!');
          setCompletingExercise(null);
          setCompletingDay(null);
          return;
        }

        const response = await axios.post(
          `${API}/workout-plans/${workoutPlan.id}/days/${dayId}/exercises/${exerciseId}/complete`
        );

        if (response.data.day_completed) {
          let message = `Treino completo! +${response.data.xp_earned} Pontos`;
          if (response.data.weekly_goal_bonus > 0) {
            message = `Treino completo! Meta semanal atingida! +${response.data.xp_earned + response.data.weekly_goal_bonus} Pontos total!`;
          }
          toast.success(
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              <span>{message}</span>
            </div>
          );
          // Show feedback modal BEFORE any other async calls
          setShowFeedbackModal(dayId);
          try {
            if (onUserUpdate) {
              const userRes = await axios.get(`${API}/users/me`);
              onUserUpdate(userRes.data);
            }
          } catch (userErr) {
            console.error('Error refreshing user:', userErr);
          }
        } else {
          toast.success('Exercício concluído! ✓');
        }
      }

      // Refresh progress
      const progressResponse = await axios.get(`${API}/workout-plans/${workoutPlan.id}/progress`);
      setProgress(progressResponse.data);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erro ao atualizar exercício';
      toast.error(errorMessage);
    } finally {
      setCompletingExercise(null);
      setCompletingDay(null);
    }
  };

  const [finishingDay, setFinishingDay] = useState(null);

  const finishEntireDay = async (day) => {
    if (isDayCompleted(day.id) || finishingDay === day.id || completingDay === day.id) return;

    const allExerciseIds = day.exercises?.map(e => e.id) || [];
    const remaining = allExerciseIds.filter(id => !isExerciseCompleted(day.id, id));

    if (remaining.length === 0) {
      toast.info('Todos os exercícios já foram concluídos!');
      return;
    }

    setFinishingDay(day.id);
    setCompletingDay(day.id);

    try {
      // Use single batch endpoint (avoids sequential N calls -> Cloudflare 520)
      const response = await axios.post(
        `${API}/workout-plans/${workoutPlan.id}/days/${day.id}/complete-all`
      );

      if (response?.data?.day_completed) {
        let message = `Treino completo! +${response.data.xp_earned} Pontos`;
        if (response.data.monthly_goal_bonus > 0) {
          message = `Treino completo! Meta mensal atingida! +${response.data.xp_earned + response.data.monthly_goal_bonus} Pontos total!`;
        }
        toast.success(
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-400" />
            <span>{message}</span>
          </div>
        );
        setShowFeedbackModal(day.id);
        try {
          if (onUserUpdate) {
            const userRes = await axios.get(`${API}/users/me`);
            onUserUpdate(userRes.data);
          }
        } catch (userErr) {
          console.error('Error refreshing user:', userErr);
        }
      }

      const progressResponse = await axios.get(`${API}/workout-plans/${workoutPlan.id}/progress`);
      setProgress(progressResponse.data);
    } catch (error) {
      const backendMsg = error?.response?.data?.detail;
      toast.error(typeof backendMsg === 'string' ? backendMsg : 'Erro ao finalizar treino. Verifique sua conexão e tente novamente.');
    } finally {
      setCompletingExercise(null);
      setCompletingDay(null);
      setFinishingDay(null);
    }
  };

  const submitFeedback = async () => {
    setSendingFeedback(true);
    try {
      // Submit feedback text if provided
      if (feedbackText.trim()) {
        await axios.post(
          `${API}/workout-plans/${workoutPlan.id}/days/${showFeedbackModal}/feedback`,
          { feedback: feedbackText }
        );
      }

      // Upload photo if selected
      if (selectedPhoto) {
        setUploadingPhoto(true);
        const formData = new FormData();
        formData.append('file', selectedPhoto);
        const caption = photoCaption.trim() || 'Treino finalizado!';
        const photoRes = await axios.post(
          `${API}/workout-photos?day_id=${showFeedbackModal}&plan_id=${workoutPlan.id}&caption=${encodeURIComponent(caption)}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,  // 60s timeout for photo upload
          }
        );
        if (photoRes.data.xp_earned > 0) {
          toast.success(`Foto postada! +${photoRes.data.xp_earned} Pontos`);
        } else {
          toast.success('Foto postada na comunidade!');
        }
        if (onUserUpdate) {
          const userRes = await axios.get(`${API}/users/me`);
          onUserUpdate(userRes.data);
        }
      }

      if (feedbackText.trim() && !selectedPhoto) {
        toast.success('Feedback enviado!');
      } else if (!feedbackText.trim() && !selectedPhoto) {
        // Nothing to send
      }

      setShowFeedbackModal(null);
      setFeedbackText('');
      setSelectedPhoto(null);
      setPhotoPreview(null);
      setPhotoCaption('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar');
    } finally {
      setSendingFeedback(false);
      setUploadingPhoto(false);
    }
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve) => {
      // If file is small enough, no compression needed
      if (file.size < 500 * 1024) { resolve(file); return; }

      const img = new Image();
      const canvas = document.createElement('canvas');
      const reader = new FileReader();

      reader.onload = (e) => {
        img.onload = () => {
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name || 'photo.jpg', { type: 'image/jpeg' }));
            } else {
              resolve(file);
            }
          }, 'image/jpeg', quality);
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 20MB.');
      return;
    }
    // Show preview immediately
    setPhotoPreview(URL.createObjectURL(file));
    // Compress in background
    const compressed = await compressImage(file);
    setSelectedPhoto(compressed);
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;

    let videoId = null;

    if (url.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('youtube.com/embed/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/shorts/')) {
      videoId = url.split('youtube.com/shorts/')[1]?.split('?')[0];
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  // Get current week info
  const getCurrentWeekInfo = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date) => {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    };
    
    return `${formatDate(monday)} - ${formatDate(sunday)}`;
  };

  // Format plan dates for display
  const formatPlanDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Calculate period progress for a day
  const calculatePeriodProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T23:59:59');
    const today = new Date();
    
    // Total days in the period
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    
    // Days passed
    const daysPassed = Math.ceil((today - start) / (1000 * 60 * 60 * 24));
    
    // Calculate percentage
    let percentage = (daysPassed / totalDays) * 100;
    percentage = Math.max(0, Math.min(100, percentage));
    
    // Days remaining
    const daysRemaining = Math.max(0, Math.ceil((end - today) / (1000 * 60 * 60 * 24)));
    
    return {
      percentage: Math.round(percentage),
      daysPassed: Math.max(0, daysPassed),
      daysRemaining,
      totalDays,
      isExpired: today > end,
      notStarted: today < start
    };
  };

  // Check if user is active
  if (!user?.is_active) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="text-center">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="font-primary font-black text-2xl text-white mb-2">
            CONTA SUSPENSA
          </h2>
          <p className="text-zinc-400">
            Entre em contato com seu treinador para mais informações.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <WorkoutSkeleton />;
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-[#050505] pb-24 px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <Dumbbell className="w-8 h-8 text-neon-lime" />
          <h1 className="font-primary font-black text-3xl text-white uppercase tracking-tighter">
            Meus Treinos
          </h1>
        </div>
        <div className="bg-zinc-900/50 border border-red-500/20 rounded-2xl p-8 text-center">
          <RefreshCw className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
          <h2 className="font-primary font-bold text-xl text-white mb-2">
            Erro ao carregar treinos
          </h2>
          <p className="text-zinc-500 mb-4">
            Verifique sua conexão e tente novamente.
          </p>
          <Button
            onClick={() => fetchWorkoutData()}
            className="bg-neon-lime text-black font-bold hover:bg-neon-lime/80"
            data-testid="retry-workout-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  if (!workoutPlan || workoutPlan.days?.length === 0) {
    return (
      <div className="min-h-screen bg-[#050505] pb-24 px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <Dumbbell className="w-8 h-8 text-neon-lime" />
          <h1 className="font-primary font-black text-3xl text-white uppercase tracking-tighter">
            Meus Treinos
          </h1>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 text-center">
          <Dumbbell className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="font-primary font-bold text-xl text-white mb-2">
            Nenhum treino disponível
          </h2>
          <p className="text-zinc-500">
            Aguarde seu treinador configurar os treinos.
          </p>
        </div>
      </div>
    );
  }

  // Count completed days this week
  const completedDaysThisWeek = progress.filter(p => p.completed_at).length;
  const totalDays = workoutPlan.days?.length || 0;

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 pt-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Dumbbell className="w-8 h-8 text-neon-lime" />
        <div>
          <h1 className="font-primary font-black text-3xl text-white uppercase tracking-tighter">
            Meus Treinos
          </h1>
          <p className="text-zinc-500 text-sm">
            Complete os exercícios e ganhe Pontos
          </p>
        </div>
      </div>

      {/* Week Info Card */}
      {/* META DO TREINO — monthly goal card */}
      {(() => {
        const goal = user?.monthly_goal || 4;
        const done = Math.min(user?.workouts_this_month || 0, goal * 2); // cap absurd values
        const remaining = Math.max(0, goal - done);
        const pct = goal > 0 ? Math.min(100, Math.round((done / goal) * 100)) : 0;
        const reached = done >= goal;
        return (
          <motion.div
            className={`border rounded-2xl p-5 mb-6 ${
              reached
                ? 'bg-gradient-to-br from-neon-lime/15 to-neon-purple/10 border-neon-lime/50'
                : 'bg-gradient-to-br from-neon-purple/10 to-neon-lime/5 border-neon-purple/40'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  reached ? 'bg-neon-lime/20' : 'bg-neon-purple/20'
                }`}>
                  <Trophy className={`w-5 h-5 ${reached ? 'text-neon-lime' : 'text-neon-purple'}`} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Meta do Treino</p>
                  <p className="text-white font-bold text-sm">Treinos deste mês</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-primary font-black text-3xl leading-none ${
                  reached ? 'text-neon-lime' : 'text-white'
                }`} data-testid="monthly-goal-progress">
                  {done}<span className="text-zinc-500 text-xl">/{goal}</span>
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="h-3 bg-zinc-900 rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${
                  reached
                    ? 'bg-gradient-to-r from-neon-lime to-neon-purple'
                    : 'bg-gradient-to-r from-neon-purple to-neon-lime'
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              />
            </div>

            <div className="mt-3 flex items-center justify-between text-xs">
              {reached ? (
                <span className="text-neon-lime font-bold flex items-center gap-1">
                  <Trophy className="w-3.5 h-3.5" />
                  Meta atingida — continue evoluindo!
                </span>
              ) : (
                <span className="text-zinc-400">
                  Faltam <span className="text-neon-lime font-bold">{remaining}</span> treino(s) para bater a meta
                </span>
              )}
              <span className="text-zinc-500 font-bold">{pct}%</span>
            </div>
          </motion.div>
        );
      })()}

      {/* Days List */}
      <div className="space-y-4">
        {workoutPlan.days
          ?.sort((a, b) => a.day_number - b.day_number)
          .map((day) => {
            const dayProgress = getDayProgress(day);
            const completed = isDayCompleted(day.id);
            const progressPercent =
              dayProgress.total > 0
                ? Math.round((dayProgress.completed / dayProgress.total) * 100)
                : 0;

            return (
              <motion.div
                key={day.id}
                className={`bg-zinc-900/50 border rounded-2xl overflow-hidden ${
                  completed
                    ? 'border-neon-lime/50'
                    : progressPercent > 0
                    ? 'border-neon-purple/30'
                    : 'border-white/5'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Day Header */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() =>
                    setExpandedDay(expandedDay === day.id ? null : day.id)
                  }
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center relative ${
                        completed
                          ? 'bg-neon-lime'
                          : progressPercent > 0
                          ? 'bg-neon-purple/30'
                          : 'bg-zinc-800'
                      }`}
                    >
                      {completed ? (
                        <CheckCircle2 className="w-7 h-7 text-black" />
                      ) : (
                        <Dumbbell
                          className={`w-7 h-7 ${
                            progressPercent > 0 ? 'text-neon-purple' : 'text-zinc-500'
                          }`}
                        />
                      )}
                    </div>
                    <div>
                      <h3 className="font-primary font-bold text-xl text-white uppercase">
                        {day.name || 'Treino'}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-sm text-zinc-500">
                          {dayProgress.completed}/{dayProgress.total} exercícios
                        </span>
                        {completed && (
                          <span className="flex items-center gap-1 text-sm text-neon-lime">
                            <Zap className="w-4 h-4" />
                            +10 Pontos
                          </span>
                        )}
                        {(day.start_date || day.end_date) && (
                          <span className="text-xs text-zinc-600">
                            {day.end_date && `até ${formatPlanDate(day.end_date)}`}
                          </span>
                        )}
                        {/* Period Progress Bar */}
                        {(() => {
                          const periodProgress = calculatePeriodProgress(day.start_date, day.end_date);
                          if (!periodProgress) return null;
                          
                          return (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-zinc-600">
                                Período: {periodProgress.daysPassed}/{periodProgress.totalDays} dias
                              </span>
                              <div className="w-16 h-1 bg-zinc-800 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all duration-300 ${
                                    periodProgress.isExpired ? 'bg-red-500' : 
                                    periodProgress.notStarted ? 'bg-zinc-600' : 'bg-neon-purple'
                                  }`}
                                  style={{ width: `${periodProgress.percentage}%` }}
                                />
                              </div>
                              <span className={`text-xs ${
                                periodProgress.isExpired ? 'text-red-400' : 
                                periodProgress.notStarted ? 'text-zinc-500' : 'text-neon-purple'
                              }`}>
                                {periodProgress.percentage}%
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Completed Badge or Progress Circle */}
                    {completed ? (
                      <div className="bg-neon-lime/20 text-neon-lime px-3 py-1 rounded-full text-xs font-bold">
                        Feito esta semana
                      </div>
                    ) : dayProgress.total > 0 && progressPercent > 0 ? (
                      <div className="relative w-10 h-10">
                        <svg className="w-10 h-10 -rotate-90">
                          <circle
                            cx="20"
                            cy="20"
                            r="16"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            className="text-zinc-800"
                          />
                          <circle
                            cx="20"
                            cy="20"
                            r="16"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            strokeDasharray={`${progressPercent} 100`}
                            className="text-neon-purple"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">
                          {progressPercent}%
                        </span>
                      </div>
                    ) : null}

                    {expandedDay === day.id ? (
                      <ChevronUp className="w-6 h-6 text-zinc-400" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-zinc-400" />
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {dayProgress.total > 0 && (
                  <div className="px-4 pb-2">
                    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full ${
                          completed ? 'bg-neon-lime' : 'bg-neon-purple'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercent}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  </div>
                )}

                {/* Expanded Content - Exercises */}
                <AnimatePresence>
                  {expandedDay === day.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-2">
                        {/* Day Already Completed Message */}
                        {completed && (
                          <motion.div
                            className="bg-neon-lime/10 border border-neon-lime/30 rounded-xl p-4 text-center mb-4"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                          >
                            <Trophy className="w-10 h-10 text-neon-lime mx-auto mb-2" />
                            <h4 className="font-primary font-bold text-lg text-neon-lime uppercase">
                              Treino Completo!
                            </h4>
                            <p className="text-zinc-400 text-sm">
                              Você já ganhou +10 Pontos esta semana. Volte na próxima semana!
                            </p>
                          </motion.div>
                        )}

                        {day.exercises?.length === 0 ? (
                          <p className="text-zinc-600 text-center py-4">
                            Nenhum exercício cadastrado
                          </p>
                        ) : (
                          <>
                            <p className="text-zinc-500 text-xs mb-3 px-1">
                              Lista de exercícios (toque no ícone para ver o vídeo)
                            </p>
                            <div className="space-y-0 divide-y divide-white/5">
                              {day.exercises
                                ?.sort((a, b) => (a.order || 0) - (b.order || 0))
                                .map((exercise, index) => {
                                  const isCompleted = isExerciseCompleted(day.id, exercise.id);
                                  const isCompleting = completingExercise === exercise.id;
                                  const isDayLocked = completingDay === day.id;
                                  const dayIsCompleted = completed;
                                  const noteKey = `${day.id}_${exercise.id}`;
                                  const isExpanded = expandedVideo === exercise.id;

                                  // Super Série
                                  if (exercise.is_super_series && exercise.sub_exercises?.length > 0) {
                                    return (
                                      <motion.div
                                        key={exercise.id}
                                        data-testid={`exercise-card-${exercise.id}`}
                                        className="py-4 first:pt-0"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.04 }}
                                      >
                                        {/* Super Series Main Row */}
                                        <div className="flex items-start gap-3">
                                          {/* Number Circle */}
                                          <button
                                            onClick={() => completeExercise(day.id, exercise.id)}
                                            disabled={isCompleting || isDayLocked || dayIsCompleted}
                                            className="flex-shrink-0 mt-0.5"
                                            data-testid={`exercise-check-${exercise.id}`}
                                          >
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                                              isCompleted
                                                ? 'bg-neon-lime shadow-[0_0_12px_rgba(204,255,0,0.3)] hover:bg-neon-lime/80 active:scale-90'
                                                : isCompleting
                                                ? 'bg-neon-lime/50 animate-pulse'
                                                : 'border-2 border-neon-purple/50 hover:bg-neon-purple/10 active:scale-90'
                                            }`}>
                                              {isCompleted ? (
                                                <CheckCircle2 className="w-5 h-5 text-black" />
                                              ) : isCompleting ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-neon-purple" />
                                              ) : null}
                                            </div>
                                          </button>

                                          {/* Content */}
                                          <div className="flex-1 min-w-0">
                                            <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded-full mb-1 ${
                                              isCompleted 
                                                ? 'bg-neon-lime/10 text-neon-lime/60' 
                                                : 'bg-neon-purple/20 text-neon-purple'
                                            }`}>
                                              {exercise.method_name || 'SUPER SÉRIE'}
                                            </span>
                                            <h4 className={`font-bold text-[15px] leading-tight ${
                                              isCompleted ? 'text-white/40 line-through' : 'text-white'
                                            }`}>
                                              {exercise.name?.replace(`${exercise.method_name}: `, '')}
                                            </h4>
                                            {exercise.execution_details && (
                                              <p className={`text-sm mt-0.5 ${isCompleted ? 'text-zinc-600' : 'text-zinc-400'}`}>
                                                {exercise.execution_details}
                                              </p>
                                            )}

                                            {/* Rest Info */}
                                            {(exercise.rest_between_exercises || exercise.rest_after_series) && (
                                              <div className="flex gap-3 mt-1 flex-wrap">
                                                {exercise.rest_between_exercises && (
                                                  <span className={`text-xs ${isCompleted ? 'text-zinc-700' : 'text-zinc-500'}`}>
                                                    Descanso: <span className="text-neon-purple/80">{exercise.rest_between_exercises}</span>
                                                  </span>
                                                )}
                                                {exercise.rest_after_series && (
                                                  <span className={`text-xs ${isCompleted ? 'text-zinc-700' : 'text-zinc-500'}`}>
                                                    Após série: <span className="text-neon-purple/80">{exercise.rest_after_series}</span>
                                                  </span>
                                                )}
                                              </div>
                                            )}

                                            {/* Sub Exercises */}
                                            <div className="mt-2 space-y-1.5">
                                              {exercise.sub_exercises.map((subEx, subIdx) => (
                                                <div key={subEx.id || subIdx}>
                                                <div className="flex items-center justify-between gap-2">
                                                  <div className="flex-1 min-w-0">
                                                    <p className={`text-sm ${isCompleted ? 'text-white/30' : 'text-zinc-300'}`}>
                                                      <span className="text-neon-purple/70 mr-1.5">{subIdx + 1}.</span>
                                                      {subEx.name}
                                                      {subEx.execution_details && (
                                                        <span className={`ml-1.5 ${isCompleted ? 'text-zinc-700' : 'text-zinc-500'}`}>
                                                          — {subEx.execution_details}
                                                        </span>
                                                      )}
                                                    </p>
                                                  </div>
                                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    {subEx.substitute_ids && subEx.substitute_ids.length > 0 && (
                                                      <button
                                                        onClick={() => setSubstitutesModal({ name: subEx.name, ids: subEx.substitute_ids })}
                                                        className="w-9 h-9 rounded-full bg-zinc-800/60 border border-zinc-700 flex items-center justify-center hover:bg-zinc-700 active:scale-90 transition-all duration-150"
                                                        title="Ver substitutos"
                                                        data-testid={`substitutes-btn-sub-${exercise.id}-${subIdx}`}
                                                      >
                                                        <Repeat className="w-4 h-4 text-zinc-300" />
                                                      </button>
                                                    )}
                                                    {subEx.youtube_url && (
                                                      <button
                                                        onClick={() => setExpandedVideo(expandedVideo === `${exercise.id}-${subIdx}` ? null : `${exercise.id}-${subIdx}`)}
                                                        className="w-9 h-9 rounded-full bg-neon-lime/10 border border-neon-lime/30 flex items-center justify-center hover:bg-neon-lime/20 active:scale-90 transition-all duration-150"
                                                        data-testid={`play-btn-${exercise.id}-${subIdx}`}
                                                      >
                                                        <Play className="w-4 h-4 text-neon-lime fill-neon-lime" />
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                                <ExerciseMovementImages exercise={{ ...subEx, id: `${exercise.id}-${subIdx}` }} dimmed={isCompleted} />
                                                </div>
                                              ))}
                                            </div>

                                            {/* Sub Exercise Videos */}
                                            {exercise.sub_exercises.map((subEx, subIdx) => (
                                              <AnimatePresence key={`video-${subEx.id || subIdx}`}>
                                                {expandedVideo === `${exercise.id}-${subIdx}` && subEx.youtube_url &&
                                                  getYoutubeEmbedUrl(subEx.youtube_url) && (
                                                    <motion.div
                                                      className="mt-3"
                                                      initial={{ height: 0, opacity: 0 }}
                                                      animate={{ height: 'auto', opacity: 1 }}
                                                      exit={{ height: 0, opacity: 0 }}
                                                      transition={{ duration: 0.3 }}
                                                    >
                                                      <div className="aspect-video rounded-xl overflow-hidden bg-black border border-white/5">
                                                        <iframe
                                                          src={getYoutubeEmbedUrl(subEx.youtube_url)}
                                                          title={subEx.name}
                                                          className="w-full h-full"
                                                          frameBorder="0"
                                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                          allowFullScreen
                                                        />
                                                      </div>
                                                    </motion.div>
                                                  )}
                                              </AnimatePresence>
                                            ))}

                                            {/* Instructions */}
                                            {exercise.instructions && (
                                              <button
                                                onClick={() => setExpandedInstruction(expandedInstruction === exercise.id ? null : exercise.id)}
                                                className="flex items-start gap-1.5 mt-2 text-left w-full group"
                                              >
                                                <Lightbulb className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 transition-colors ${
                                                  expandedInstruction === exercise.id 
                                                    ? 'text-neon-lime' 
                                                    : isCompleted ? 'text-zinc-700' : 'text-neon-lime/50 group-hover:text-neon-lime/80'
                                                }`} />
                                                <p className={`text-xs italic leading-relaxed ${isCompleted ? 'text-zinc-700' : 'text-zinc-500'} ${
                                                  expandedInstruction === exercise.id ? '' : 'line-clamp-1'
                                                }`}>
                                                  {exercise.instructions}
                                                </p>
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        {/* Notes - colapsável */}
                                        <div className="mt-3 ml-12">
                                          <div className="flex gap-2 items-center">
                                            <Input
                                              placeholder="Anotação: peso, ajuste..."
                                              defaultValue={exerciseNotes[noteKey] || ''}
                                              onBlur={(e) => {
                                                const newValue = e.target.value;
                                                const currentValue = exerciseNotes[noteKey] || '';
                                                if (newValue !== currentValue) {
                                                  saveExerciseNote(day.id, exercise.id, newValue);
                                                }
                                              }}
                                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                              className="bg-zinc-950/50 border-zinc-800 text-white text-xs h-8 placeholder:text-zinc-700"
                                            />
                                            {savingNote === noteKey && (
                                              <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-neon-lime flex-shrink-0" />
                                            )}
                                          </div>

                                          {/* Iniciar Descanso + Substitutos */}
                                          <div className="flex flex-wrap items-center gap-2 mt-2">
                                            <button
                                              onClick={() => setRestPickerFor({ id: exercise.id, name: exercise.name })}
                                              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neon-lime/80 hover:text-neon-lime bg-neon-lime/5 hover:bg-neon-lime/10 border border-neon-lime/20 rounded-full px-3 py-1.5 transition-all active:scale-95"
                                              data-testid={`rest-start-${exercise.id}`}
                                            >
                                              <Timer className="w-3.5 h-3.5" />
                                              Iniciar Descanso
                                            </button>
                                            {exercise.substitute_ids && exercise.substitute_ids.length > 0 && (
                                              <button
                                                onClick={() => setSubstitutesModal({ name: exercise.name, ids: exercise.substitute_ids })}
                                                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-white bg-zinc-800/60 hover:bg-zinc-700 border border-zinc-700 rounded-full px-3 py-1.5 transition-all active:scale-95"
                                                data-testid={`substitutes-btn-${exercise.id}`}
                                              >
                                                <Repeat className="w-3.5 h-3.5" />
                                                Substitutos
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </motion.div>
                                    );
                                  }

                                  // ── Exercício Regular ──
                                  return (
                                    <motion.div
                                      key={exercise.id}
                                      data-testid={`exercise-card-${exercise.id}`}
                                      className="py-4 first:pt-0"
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: index * 0.04 }}
                                    >
                                      <div className="flex items-start gap-3">
                                        {/* Number Circle / Complete Button */}
                                        <button
                                          onClick={() => completeExercise(day.id, exercise.id)}
                                          disabled={isCompleting || isDayLocked || dayIsCompleted}
                                          className="flex-shrink-0 mt-0.5"
                                          data-testid={`exercise-check-${exercise.id}`}
                                        >
                                          <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                                            isCompleted
                                              ? 'bg-neon-lime shadow-[0_0_12px_rgba(204,255,0,0.3)] hover:bg-neon-lime/80 active:scale-90'
                                              : isCompleting
                                              ? 'bg-neon-lime/50 animate-pulse'
                                              : 'border-2 border-neon-lime/40 hover:bg-neon-lime/10 active:scale-90'
                                          }`}>
                                            {isCompleted ? (
                                              <CheckCircle2 className="w-5 h-5 text-black" />
                                            ) : isCompleting ? (
                                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-neon-lime" />
                                            ) : null}
                                          </div>
                                        </button>

                                        {/* Exercise Info */}
                                        <div className="flex-1 min-w-0">
                                          <button
                                            onClick={() => setHistoryModal({ id: exercise.id, name: exercise.name })}
                                            className={`text-left font-bold text-[15px] leading-tight hover:text-neon-lime transition-colors ${
                                              isCompleted ? 'text-white/40 line-through' : 'text-white'
                                            }`}
                                            data-testid={`exercise-name-${exercise.id}`}
                                          >
                                            {exercise.name}
                                            <TrendingUp className="inline-block w-3 h-3 ml-1.5 text-neon-lime/40 align-middle" />
                                          </button>
                                          {exercise.execution_details && (
                                            <p className={`text-sm mt-0.5 ${isCompleted ? 'text-zinc-600' : 'text-zinc-400'}`}>
                                              {exercise.execution_details}
                                            </p>
                                          )}
                                          {exercise.instructions && (
                                            <button
                                              onClick={() => setExpandedInstruction(expandedInstruction === exercise.id ? null : exercise.id)}
                                              className="flex items-start gap-1.5 mt-1.5 text-left w-full group"
                                            >
                                              <Lightbulb className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 transition-colors ${
                                                expandedInstruction === exercise.id
                                                  ? 'text-neon-lime'
                                                  : isCompleted ? 'text-zinc-700' : 'text-neon-lime/50 group-hover:text-neon-lime/80'
                                              }`} />
                                              <p className={`text-xs italic leading-relaxed ${isCompleted ? 'text-zinc-700' : 'text-zinc-500'} ${
                                                expandedInstruction === exercise.id ? '' : 'line-clamp-1'
                                              }`}>
                                                {exercise.instructions}
                                              </p>
                                            </button>
                                          )}
                                        </div>

                                        {/* Play Button */}
                                        {exercise.youtube_url ? (
                                          <button
                                            onClick={() => setExpandedVideo(isExpanded ? null : exercise.id)}
                                            className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90 ${
                                              isExpanded
                                                ? 'bg-neon-lime text-black shadow-[0_0_16px_rgba(204,255,0,0.4)]'
                                                : 'bg-neon-lime/10 border-2 border-neon-lime/30 hover:bg-neon-lime/20'
                                            }`}
                                            data-testid={`play-btn-${exercise.id}`}
                                          >
                                            <Play className={`w-5 h-5 ${isExpanded ? 'text-black fill-black' : 'text-neon-lime fill-neon-lime'}`} />
                                          </button>
                                        ) : (
                                          <div className="flex-shrink-0 w-11 h-11" />
                                        )}
                                      </div>

                                      <ExerciseMovementImages exercise={exercise} dimmed={isCompleted} className="mt-3 ml-12" />

                                      {/* YouTube Video - expandido */}
                                      <AnimatePresence>
                                        {isExpanded && exercise.youtube_url &&
                                          getYoutubeEmbedUrl(exercise.youtube_url) && (
                                            <motion.div
                                              className="mt-3 ml-12"
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: 'auto', opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.3 }}
                                            >
                                              <div className="aspect-video rounded-xl overflow-hidden bg-black border border-neon-lime/20">
                                                <iframe
                                                  src={getYoutubeEmbedUrl(exercise.youtube_url)}
                                                  title={exercise.name}
                                                  className="w-full h-full"
                                                  frameBorder="0"
                                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                  allowFullScreen
                                                />
                                              </div>
                                            </motion.div>
                                          )}
                                      </AnimatePresence>

                                      {/* Notes */}
                                      <div className="mt-2 ml-12">
                                        {/* Load inputs (Progressive Overload) */}
                                        <div className="flex items-center gap-2 mb-2">
                                          <div className="flex items-center bg-zinc-950/60 border border-zinc-800 rounded-lg overflow-hidden">
                                            <input
                                              type="number"
                                              step="0.5"
                                              min="0"
                                              placeholder="0"
                                              defaultValue={exerciseLoads[exercise.id]?.weight_kg ?? ''}
                                              onBlur={(e) => {
                                                const v = e.target.value === '' ? null : parseFloat(e.target.value);
                                                const prevW = exerciseLoads[exercise.id]?.weight_kg ?? null;
                                                if (v !== prevW) {
                                                  saveExerciseLoad(day.id, exercise.id, { weight_kg: v, reps: exerciseLoads[exercise.id]?.reps ?? null });
                                                }
                                              }}
                                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                              className="w-14 bg-transparent text-white text-sm font-bold text-center h-8 focus:outline-none placeholder:text-zinc-700"
                                              data-testid={`load-weight-${exercise.id}`}
                                            />
                                            <span className="text-zinc-500 text-[10px] font-bold uppercase pr-2">kg</span>
                                          </div>
                                          <span className="text-zinc-700 text-sm">×</span>
                                          <div className="flex items-center bg-zinc-950/60 border border-zinc-800 rounded-lg overflow-hidden">
                                            <input
                                              type="number"
                                              min="0"
                                              placeholder="0"
                                              defaultValue={exerciseLoads[exercise.id]?.reps ?? ''}
                                              onBlur={(e) => {
                                                const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                                                const prevR = exerciseLoads[exercise.id]?.reps ?? null;
                                                if (v !== prevR) {
                                                  saveExerciseLoad(day.id, exercise.id, { weight_kg: exerciseLoads[exercise.id]?.weight_kg ?? null, reps: v });
                                                }
                                              }}
                                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                              className="w-12 bg-transparent text-white text-sm font-bold text-center h-8 focus:outline-none placeholder:text-zinc-700"
                                              data-testid={`load-reps-${exercise.id}`}
                                            />
                                            <span className="text-zinc-500 text-[10px] font-bold uppercase pr-2">reps</span>
                                          </div>
                                          {savingLoad === exercise.id && (
                                            <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-neon-lime flex-shrink-0" />
                                          )}
                                          <button
                                            onClick={() => setHistoryModal({ id: exercise.id, name: exercise.name })}
                                            className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-neon-lime/70 hover:text-neon-lime transition-colors"
                                            data-testid={`view-history-${exercise.id}`}
                                          >
                                            <TrendingUp className="w-3 h-3" />
                                            Evolução
                                          </button>
                                        </div>

                                        <div className="flex gap-2 items-center">
                                          <Input
                                            placeholder="Anotação: ajustes, sensação..."
                                            defaultValue={exerciseNotes[noteKey] || ''}
                                            onBlur={(e) => {
                                              const newValue = e.target.value;
                                              const currentValue = exerciseNotes[noteKey] || '';
                                              if (newValue !== currentValue) {
                                                saveExerciseNote(day.id, exercise.id, newValue);
                                              }
                                            }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                            className="bg-zinc-950/50 border-zinc-800 text-white text-xs h-8 placeholder:text-zinc-700"
                                          />
                                          {savingNote === noteKey && (
                                            <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-neon-lime flex-shrink-0" />
                                          )}
                                        </div>

                                        {/* Iniciar Descanso + Substitutos */}
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                          <button
                                            onClick={() => setRestPickerFor({ id: exercise.id, name: exercise.name })}
                                            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-neon-lime/80 hover:text-neon-lime bg-neon-lime/5 hover:bg-neon-lime/10 border border-neon-lime/20 rounded-full px-3 py-1.5 transition-all active:scale-95"
                                            data-testid={`rest-start-${exercise.id}`}
                                          >
                                            <Timer className="w-3.5 h-3.5" />
                                            Iniciar Descanso
                                          </button>
                                          {exercise.substitute_ids && exercise.substitute_ids.length > 0 && (
                                            <button
                                              onClick={() => setSubstitutesModal({ name: exercise.name, ids: exercise.substitute_ids })}
                                              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-300 hover:text-white bg-zinc-800/60 hover:bg-zinc-700 border border-zinc-700 rounded-full px-3 py-1.5 transition-all active:scale-95"
                                              data-testid={`substitutes-btn-${exercise.id}`}
                                            >
                                              <Repeat className="w-3.5 h-3.5" />
                                              Substitutos
                                              <span className="ml-1 text-[10px] bg-zinc-700 text-zinc-300 rounded-full px-1.5 py-0.5">
                                                {exercise.substitute_ids.length}
                                              </span>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </motion.div>
                                  );
                                })}
                            </div>
                          </>
                        )}

                        {/* Finalizar Treino Button */}
                        {!completed && day.exercises?.length > 0 && (
                          <motion.div
                            className="mt-4 pt-4 border-t border-zinc-800/50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <Button
                              onClick={() => finishEntireDay(day)}
                              disabled={finishingDay === day.id || completingDay === day.id}
                              className="w-full bg-neon-lime text-black font-black uppercase py-3 rounded-lg hover:bg-neon-lime/80 transition-colors disabled:opacity-50"
                              data-testid={`finish-day-${day.id}`}
                            >
                              {finishingDay === day.id ? (
                                <div className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-current" />
                                  Finalizando...
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-2">
                                  <CheckCircle2 className="w-5 h-5" />
                                  Finalizar Treino ({getDayProgress(day).completed}/{getDayProgress(day).total})
                                </div>
                              )}
                            </Button>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
      </div>

      {/* Motivational Footer */}
      <motion.div
        className="mt-8 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <p className="text-zinc-600 text-sm">
          💪 Cada treino completado = +10 Pontos
        </p>
      </motion.div>

      {/* Feedback Modal */}
      <AnimatePresence>
        {showFeedbackModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-[60]"
            onClick={() => { setShowFeedbackModal(null); setSelectedPhoto(null); setPhotoPreview(null); setPhotoCaption(''); }}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-zinc-900 border-t sm:border border-neon-lime/20 sm:rounded-2xl rounded-t-2xl p-5 pb-24 sm:pb-5 w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neon-lime/20 rounded-full flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-neon-lime" />
                  </div>
                  <div>
                    <h3 className="font-primary font-bold text-lg text-white">Treino Completo!</h3>
                    <p className="text-xs text-zinc-500">Registre seu treino</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowFeedbackModal(null); setSelectedPhoto(null); setPhotoPreview(null); setPhotoCaption(''); }}
                  className="text-zinc-500 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Photo Section */}
              <div className="mb-4">
                <p className="text-sm text-zinc-400 mb-2 flex items-center gap-1.5">
                  <Camera className="w-4 h-4 text-neon-lime" />
                  Registre com uma foto (+10 Pontos)
                </p>
                {photoPreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-neon-lime/20">
                    <img src={photoPreview} alt="Preview" className="w-full h-48 object-cover" />
                    <button
                      onClick={() => { setSelectedPhoto(null); setPhotoPreview(null); setPhotoCaption(''); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-black/70 rounded-full flex items-center justify-center hover:bg-black"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-zinc-700 hover:border-neon-lime/40 rounded-xl cursor-pointer transition-colors bg-zinc-950/50">
                    <Camera className="w-8 h-8 text-zinc-600 mb-2" />
                    <span className="text-sm text-zinc-500">Toque para tirar ou escolher foto</span>
                    <span className="text-xs text-zinc-600 mt-1">Opcional</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoSelect}
                      data-testid="photo-input"
                    />
                  </label>
                )}
              </div>

              {/* Photo Caption - shown when photo selected */}
              {photoPreview && (
                <div className="mb-4">
                  <p className="text-sm text-zinc-400 mb-2 flex items-center gap-1.5">
                    <PenLine className="w-4 h-4 text-neon-lime" />
                    Legenda da foto (opcional)
                  </p>
                  <input
                    type="text"
                    value={photoCaption}
                    onChange={(e) => setPhotoCaption(e.target.value)}
                    placeholder="Ex: Dia de treino pesado! 💪"
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-neon-lime/50 text-white rounded-xl p-3 placeholder:text-zinc-600 text-sm"
                    data-testid="photo-caption-input"
                  />
                </div>
              )}

              {/* Feedback Text */}
              <div className="mb-4">
                <p className="text-sm text-zinc-400 mb-2 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-zinc-500" />
                  Feedback para o treinador (opcional)
                </p>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Ex: Senti dor no joelho durante o agachamento..."
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-neon-lime/50 text-white rounded-xl p-3 min-h-[80px] resize-none placeholder:text-zinc-600 text-sm"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => { setShowFeedbackModal(null); setFeedbackText(''); setSelectedPhoto(null); setPhotoPreview(null); setPhotoCaption(''); }}
                  variant="ghost"
                  className="flex-1 text-zinc-400 hover:text-white"
                >
                  Pular
                </Button>
                <Button
                  onClick={submitFeedback}
                  disabled={sendingFeedback || (!feedbackText.trim() && !selectedPhoto)}
                  className="flex-1 bg-neon-lime text-black font-bold hover:bg-neon-lime/80 disabled:opacity-50"
                  data-testid="submit-feedback-btn"
                >
                  {sendingFeedback ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current" />
                      {uploadingPhoto ? 'Enviando foto...' : 'Enviando...'}
                    </div>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {selectedPhoto ? 'Postar Foto' : feedbackText.trim() ? 'Enviar' : 'Enviar'}
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rest Timer Picker */}
      <RestTimerPicker
        open={!!restPickerFor}
        defaultSeconds={lastRestSeconds}
        onClose={() => setRestPickerFor(null)}
        onStart={(sec) => {
          setLastRestSeconds(sec);
          setRestTimer({
            seconds: sec,
            exerciseName: restPickerFor?.name || '',
            key: Date.now(),
          });
          setRestPickerFor(null);
        }}
      />

      {/* Rest Timer Bottom Bar */}
      <AnimatePresence>
        {restTimer && (
          <RestTimerBar
            key={restTimer.key}
            timerKey={restTimer.key}
            seconds={restTimer.seconds}
            exerciseName={restTimer.exerciseName}
            onCancel={() => { clearRestState(); setRestTimer(null); }}
          />
        )}
      </AnimatePresence>

      {/* Exercise Load History Modal */}
      <ExerciseHistoryModal
        open={!!historyModal}
        exerciseId={historyModal?.id}
        exerciseName={historyModal?.name || ''}
        onClose={() => setHistoryModal(null)}
      />

      {/* Substitutes Modal */}
      <SubstitutesModal
        open={!!substitutesModal}
        exerciseName={substitutesModal?.name || ''}
        substituteIds={substitutesModal?.ids || []}
        onClose={() => setSubstitutesModal(null)}
      />
    </div>
  );
};
